/**
 * Index symbol key handling.
 *
 * The same index is spelled differently by each producer:
 *  - the live feed publishes `symbol` (e.g. "NIFTY", "BANKNIFTY", "India VIX")
 *  - `index_ticks.name` holds the exchange name (e.g. "NIFTY 50", "NIFTY BANK")
 *  - `indexdata.symbol` (Market Analysis) uses UI names (e.g. "NIFTY 50", "BANK NIFTY")
 *
 * Every lookup goes through `normalizeIndexKey`, so spacing/case differences resolve
 * on their own ("Bank Nifty" -> "BANKNIFTY"). Only genuinely different spellings need
 * an entry in `INDEX_KEY_ALIASES`.
 */

/** Canonical form of any index name/symbol: upper case, alphanumerics only. */
export const normalizeIndexKey = (value: string | null | undefined): string =>
  value ? value.toUpperCase().replace(/[^A-Z0-9]/g, "") : "";

/** Normalized spelling -> normalized canonical key. */
export const INDEX_KEY_ALIASES: Record<string, string> = {
  NIFTY50: "NIFTY",
  NIFTYBANK: "BANKNIFTY",
  BANKNIFTY: "BANKNIFTY",
  NIFTYFINSERVICE: "FINNIFTY",
  NIFTYMIDCAPSELECT: "MIDCPNIFTY",
  VIX: "INDIAVIX",
};

/**
 * Every key an index tick should be reachable by, de-duplicated.
 * Used both when storing a tick and when reading one, so either side of an alias hits.
 */
export const indexKeyVariants = (
  ...values: (string | null | undefined)[]
): string[] => {
  const keys = new Set<string>();

  for (const value of values) {
    const key = normalizeIndexKey(value);
    if (!key) continue;

    keys.add(key);

    const alias = INDEX_KEY_ALIASES[key];
    if (alias) keys.add(alias);

    for (const [spelling, canonical] of Object.entries(INDEX_KEY_ALIASES)) {
      if (canonical === key) keys.add(spelling);
    }
  }

  return [...keys];
};
